import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Grid,
  IconButton,
} from '@material-ui/core';
import { Warning, Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

interface IncompleteTask {
  taskId: string;
  title: string;
  status: 'pending' | 'approved' | 'none';
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  stageName: string;
  incompleteTasks: IncompleteTask[];
}

const IncompleteTasksConfirmDialog: React.FC<Props> = ({ open, onClose, onConfirm, stageName, incompleteTasks }) => {
  const { theme } = useCampfireTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between'>
          <Grid item>
            <Box display='flex' alignItems='center'>
              <Warning style={{ marginRight: 8, color: '#FBBF24' }} />
              <Typography
                style={{
                  fontSize: '19px',
                  fontWeight: 550,
                  color: theme.color.grey.neutral400,
                }}
              >
                Incomplete Tasks in {stageName}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon color='action' />
            </IconButton>
          </Grid>
        </Grid>
        <Box mt={1}>
          <Typography style={{ fontSize: 14, color: theme.color.grey.neutral300 }}>
            This stage has {incompleteTasks.length} incomplete task{incompleteTasks.length === 1 ? '' : 's'}. Are you
            sure you want to unlock the next stage?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box
          style={{
            background: theme.color.white.neutral50,
            border: '1px solid #DCDCDC',
            borderRadius: '6px',
            padding: '12px',
          }}
        >
          <List dense style={{ paddingTop: 0, paddingBottom: 0 }}>
            {incompleteTasks.map((task, index) => (
              <ListItem
                key={task.taskId}
                style={{
                  border: '0.6px solid #00000010',
                  margin: index === 0 ? '0 0 8px 0' : index === incompleteTasks.length - 1 ? '0' : '0 0 8px 0',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  padding: '15px',
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      style={{
                        fontWeight: 600,
                        fontSize: '15px',
                        color: '#444',
                      }}
                    >
                      {task.title}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant='body2'
                      style={{
                        fontSize: '13px',
                        color: '#808080',
                      }}
                    >
                      {task.status === 'pending'
                        ? 'Pending review'
                        : task.status === 'none'
                        ? 'Not started'
                        : 'Approved'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box padding={2}>
          <TabletButton onClick={onClose} color='default' variant='text' style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton onClick={onConfirm} color='primary' variant='contained'>
            Unlock Next Stage
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export { IncompleteTasksConfirmDialog };
